import type {
  Availability,
  BlockPhase,
  MusclePriorities,
  SessionPlan,
  SyncColumns,
  TrainingTimeOfDay,
  VolumeTargets,
} from '@cadencia/contracts';
import {
  sessionExerciseSchema,
  setPrescriptionSchema,
  trainingBlockSchema,
  trainingSessionSchema,
  trainingWeekSchema,
  warmupPrescriptionSchema,
  type SessionExercise,
  type SetPrescription,
  type TrainingBlock,
  type TrainingSession,
  type TrainingWeek,
  type WarmupPrescription,
} from '@cadencia/contracts';
import {
  accumulationWeeksLeftOf,
  buildPhaseBlock,
  dayOffsets,
  chooseTechnique,
  expandSets,
  fitWeeklyVolume,
  placeCardio,
  planWeeklyCardio,
  findExercise,
  requireExercise,
  weeklyVolumeByMuscle,
  type BuiltBlock,
  type CardioSlot,
  type Muscle,
  type ResolvedSession,
  type VolumeFitChange,
  type WeekSession,
} from '@cadencia/domain';
import { db } from '../db.js';
import { logger } from '../../lib/logger.js';
import { addDays, startOfWeek, todayLocal } from '../../lib/dates.js';
import {
  newSyncColumns,
  putNewRows,
  putRow,
  type LocalTable,
  type WriteContext,
} from '../writes.js';
import { listEquipment, getDefaultGym } from './gymRepo.js';
import { getProfile } from './profileRepo.js';
import { saveCardioPrescriptions } from './cardioRepo.js';
import { parchear } from '../parchear.js';

/**
 * Repositorio de programacion.
 *
 * Las semanas se materializan DE UNA EN UNA: el contenido de una semana depende
 * de como fue la anterior, asi que crear las 4 por adelantado seria trabajo
 * tirado en cuanto el motor adaptativo (Fase 3) empiece a replanificar.
 */

/**
 * Rellena los campos que una fila vieja no tiene.
 *
 * Una columna añadida con valor por defecto NO cambia `sync_seq` ni
 * `updated_at`, asi que la fila del servidor se arregla sola pero la copia
 * local nunca se vuelve a bajar: se queda sin el campo para siempre. El
 * contrato ya declara los valores por defecto; basta con volver a pasarla por
 * el esquema al leerla.
 *
 * Con `safeParse` y no con `parse`, y el motivo no es de estilo. Esta funcion
 * esta en la ruta de LECTURA de `getActiveBlock`, que es lo primero que hacen
 * la pestaña Entrenar y la de prioridades a traves de `useLiveQuery`; y
 * `useLiveQuery` relanza en el render la excepcion de su consulta. Con `parse`,
 * UNA fila de bloque que no encajara con el contrato —una clave de musculo que
 * ya no existe, un objetivo de volumen fuera de rango, un `null` donde el
 * esquema espera un objeto— dejaba las dos pantallas en blanco y sin mensaje.
 *
 * La politica es la misma que `data/normalizacion.ts` fija para toda lectura:
 * se registra y se devuelve la fila tal cual. Descartarla perderia el bloque
 * del usuario; hacerla estallar deja la aplicacion inutilizable por una fila
 * rara. Lo que no puede pasar es que no se entere nadie.
 *
 * Ojo con el alcance: la base de datos NO valida las claves de
 * `muscle_priorities` ni de `volume_targets` (migraciones 0009 y 0013), asi que
 * una clave que el cliente no reconozca es una fila legal en el servidor y
 * llega aqui por sincronizacion. Ese caso tiene que degradarse, no reventar.
 */
function normalizarBloque(row: TrainingBlock): TrainingBlock {
  const resultado = trainingBlockSchema.safeParse(row);
  if (resultado.success) return resultado.data;

  logger.warn('db', 'BLOCK_SHAPE_MISMATCH', 'Bloque local que no encaja con su contrato', {
    id: row.id,
    problemas: resultado.error.issues
      .slice(0, 5)
      .map((issue) => `${issue.path.join('.')}: ${issue.code}`),
  });
  return row;
}

export async function getActiveBlock(userId: string): Promise<TrainingBlock | undefined> {
  const blocks = await db.trainingBlocks.where('userId').equals(userId).toArray();
  const activo = blocks.find((block) => block.deletedAt === null && block.status === 'active');
  return activo === undefined ? undefined : normalizarBloque(activo);
}

/**
 * Bloques que solapan con `[desde, hasta]`, en orden de comienzo.
 *
 * Lo usa la calibracion de nutricion para su cuarta condicion de validez
 * (`NUTRITION_ENGINE.md` §2.3, «Sin cambio de fase en la ventana»): la fase
 * decide el ajuste energetico, asi que un TDEE observado a caballo entre dos
 * fases mezcla dos lineas base distintas.
 *
 * Incluye los bloques cerrados: la ventana mira hacia atras, y el bloque que
 * termino hace diez dias es justo el que interesa.
 */
export async function blocksOverlapping(
  userId: string,
  desde: string,
  hasta: string,
): Promise<TrainingBlock[]> {
  const bloques = await db.trainingBlocks.where('userId').equals(userId).toArray();
  return bloques
    .filter(
      (bloque) =>
        bloque.deletedAt === null &&
        bloque.startedOn <= hasta &&
        (bloque.endedOn === null || bloque.endedOn >= desde),
    )
    .map(normalizarBloque)
    .sort((a, b) => a.startedOn.localeCompare(b.startedOn));
}

export async function listWeeks(blockId: string): Promise<TrainingWeek[]> {
  const weeks = await db.trainingWeeks.where('blockId').equals(blockId).toArray();
  return weeks.filter((week) => week.deletedAt === null).sort((a, b) => a.weekIndex - b.weekIndex);
}

export async function listSessionsOfWeek(weekId: string): Promise<TrainingSession[]> {
  const sessions = await db.trainingSessions.where('weekId').equals(weekId).toArray();
  return sessions
    .filter((session) => session.deletedAt === null)
    .sort((a, b) => a.orderInWeek - b.orderInWeek);
}

export async function getSessionByDate(
  userId: string,
  date: string,
): Promise<TrainingSession | undefined> {
  const sessions = await db.trainingSessions.where('scheduledDate').equals(date).toArray();
  return sessions.find((session) => session.userId === userId && session.deletedAt === null);
}

export async function getNextSession(userId: string): Promise<TrainingSession | undefined> {
  const today = todayLocal();
  const sessions = await db.trainingSessions.where('userId').equals(userId).toArray();
  return sessions
    .filter(
      (session) =>
        session.deletedAt === null &&
        session.scheduledDate >= today &&
        (session.status === 'planned' || session.status === 'in_progress'),
    )
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate))[0];
}

/**
 * Todo lo que hace falta para EJECUTAR una sesion.
 *
 * Llevaba solo la sesion y sus ejercicios, y eso dejaba fuera la mitad de la
 * pregunta: la carga que toca hoy no depende solo de que ejercicio es, sino de
 * que INTENCION tiene la semana y en que FASE esta el bloque. Sin esos dos
 * datos, `SesionActivaPage` no tenia de donde sacarlos y los cableaba
 * —`reintroduction` y readaptacion siempre—, asi que acumulacion,
 * intensificacion y descarga no llegaban nunca al motor: el bloque decidia una
 * cosa y la pantalla le contaba otra (ARCH-001 y BUG-002 de la auditoria).
 *
 * La composicion se hace AQUI y no en la pantalla a proposito: quien conoce
 * Dexie es el repositorio, y una pantalla que consulta tres tablas por su
 * cuenta es la forma habitual de que la cuarta se quede sin consultar.
 *
 * `week` y `block` son anulables porque la base de datos puede no tenerlos: una
 * fila borrada a medias o una sincronizacion incompleta dejan una sesion
 * huerfana. No se inventa una semana para tapar el hueco —eso es exactamente lo
 * que hacia el literal— pero tampoco se impide entrenar: se deja el nulo, se
 * registra, y quien decide elige que hacer con el.
 */
export interface SessionDetail {
  session: TrainingSession;
  /** La semana del programa a la que pertenece. `null` si la fila no esta. */
  week: TrainingWeek | null;
  /** El bloque al que pertenece. `null` si la fila no esta. */
  block: TrainingBlock | null;
  exercises: {
    sessionExercise: SessionExercise;
    prescriptions: SetPrescription[];
    warmups: WarmupPrescription[];
  }[];
}

export async function getSessionDetail(sessionId: string): Promise<SessionDetail | null> {
  const session = await db.trainingSessions.get(sessionId);
  if (!session || session.deletedAt !== null) return null;

  // Por clave primaria las dos: `weekId` y `blockId` son columnas de la propia
  // sesion, asi que esto son dos `get` y no dos barridos de tabla.
  const [weekRow, blockRow] = await Promise.all([
    db.trainingWeeks.get(session.weekId),
    db.trainingBlocks.get(session.blockId),
  ]);
  const week = weekRow === undefined || weekRow.deletedAt !== null ? null : weekRow;
  const block =
    blockRow === undefined || blockRow.deletedAt !== null ? null : normalizarBloque(blockRow);

  if (week === null || block === null) {
    // Ruidoso a proposito. Sin semana no hay intencion y sin bloque no hay
    // fase, asi que el motor va a recomendar con el criterio conservador; que
    // eso pase sin dejar rastro es como el literal duro sobrevivio una fase
    // entera. El usuario puede entrenar igual.
    logger.warn('db', 'SESSION_CONTEXT_MISSING', 'Sesion sin semana o sin bloque', {
      sessionId,
      weekId: session.weekId,
      blockId: session.blockId,
      falta: week === null ? (block === null ? 'semana y bloque' : 'semana') : 'bloque',
    });
  }

  const sessionExercises = (await db.sessionExercises.where('sessionId').equals(sessionId).toArray())
    .filter((item) => item.deletedAt === null)
    .sort((a, b) => a.orderIndex - b.orderIndex);

  const exercises = await Promise.all(
    sessionExercises.map(async (sessionExercise) => ({
      sessionExercise,
      prescriptions: (
        await db.setPrescriptions.where('sessionExerciseId').equals(sessionExercise.id).toArray()
      )
        .filter((item) => item.deletedAt === null)
        .sort((a, b) => a.setIndex - b.setIndex),
      warmups: (
        await db.warmupPrescriptions.where('sessionExerciseId').equals(sessionExercise.id).toArray()
      )
        .filter((item) => item.deletedAt === null)
        .sort((a, b) => a.orderIndex - b.orderIndex),
    })),
  );

  return { session, week, block, exercises };
}

// --------------------------------------------------------------------------
// Creacion del bloque
// --------------------------------------------------------------------------

export interface CreateBlockResult {
  block: TrainingBlock;
  builtBlock: BuiltBlock;
  warnings: string[];
}

/**
 * Crea el bloque de readaptacion a partir del equipamiento REAL del gimnasio y
 * materializa su primera semana.
 */
export async function createReadaptationBlock(
  ctx: WriteContext,
  options: { startDate?: string } = {},
): Promise<CreateBlockResult> {
  // El primer bloque es SIEMPRE de readaptacion (§2.3): no se negocia con la
  // composicion corporal, y ademas es el unico que el documento tabula.
  return createBlockForPhase(ctx, 'readaptation', options);
}

/**
 * Crea un bloque de la fase indicada y materializa su primera semana.
 *
 * Generaliza lo que antes solo sabia hacer readaptacion. Sin esto, terminar las
 * cuatro semanas del primer bloque dejaba la aplicacion sin nada que ofrecer:
 * nada cerraba el bloque y nada sabia crear el siguiente.
 */
export async function createBlockForPhase(
  ctx: WriteContext,
  phase: BlockPhase,
  options: {
    startDate?: string;
    musclePriorities?: MusclePriorities;
    /**
     * Ejercicios preferidos por sesion, **ya validados por el dominio**
     * (`chooseExercises` en `exerciseSelectionService`). Ausente = el motor
     * elige, que es lo que hacen las rutas automaticas.
     *
     * Entra por parametro y no se pide aqui a proposito: este modulo es un
     * repositorio y no sale a la red. Que una funcion que escribe en Dexie
     * pudiera quedarse esperando a una API es exactamente lo que la regla de
     * «guardar nunca espera a la red» existe para impedir.
     */
    preferredExercises?: (sessionName: string) => readonly string[];
    /** Semanas del bloque, si se ha propuesto un numero distinto del tabulado. */
    weeks?: number;
    /**
     * Objetivos de volumen con los que NACE el bloque.
     *
     * Hasta ahora siempre `{}`: la primera semana salia de la plantilla y el
     * motor de volumen los iba fijando a partir de lo entrenado. Un bloque
     * propuesto puede traerlos ya puestos, y entonces `fitWeeklyVolume` los
     * respeta desde la primera semana en vez de tardar un mes en llegar.
     */
    volumeTargets?: VolumeTargets;
    /**
     * Sesiones diseñadas por la IA, **ya validadas** (`validateSessionPlan`).
     *
     * Ausente = las pone la plantilla determinista de la fase, que es lo que
     * hacen todas las rutas cuando la IA no esta disponible.
     *
     * Se GUARDA en el bloque ademas de usarse aqui, y esa es la parte que no
     * se puede olvidar: `ensureWeekMaterialized` reconstruye el bloque en vez
     * de leerlo, asi que sin persistirlo la semana 2 saldria con las sesiones
     * de la plantilla y el diseño se perderia a mitad de bloque.
     */
    sessionPlan?: SessionPlan;
  } = {},
): Promise<CreateBlockResult> {
  const gym = await getDefaultGym(ctx.userId);
  const equipment = gym ? await listEquipment(gym.id) : [];
  const kinds = [...new Set(equipment.map((item) => item.kind))];
  // Lo que el atleta declaró en el test inicial. Sin perfil o sin test, `null`
  // y `[]`: el bloque de siempre, de cuatro días (docs/TEST_INICIAL.md §2.2).
  const perfil = await getProfile(ctx.userId);
  const availability = perfil?.availability ?? null;

  const built = buildPhaseBlock(phase, {
    availableEquipmentKinds: kinds,
    musclePriorities: options.musclePriorities,
    preferredExercises: options.preferredExercises,
    weeks: options.weeks,
    sessions: options.sessionPlan,
    availability,
    limitations: perfil?.limitations ?? [],
  });
  const startDate = startOfWeek(options.startDate ?? todayLocal());

  const block = trainingBlockSchema.parse({
    ...newSyncColumns(ctx),
    name: built.name,
    phase: built.phase,
    split: built.split,
    plannedWeeks: built.plannedWeeks,
    actualWeeks: null,
    startedOn: startDate,
    endedOn: null,
    status: 'active',
    objectives: built.objectives,
    exitMetrics: built.exitMetrics,
    sessionsPerWeek: built.sessionsPerWeek,
    maxSessionMinutes: built.maxSessionMinutes,
    musclePriorities: built.musclePriorities,
    // Sin objetivos declarados, la primera semana sale de la plantilla y el
    // motor de volumen los ira fijando a partir de lo que se entrene. Con una
    // propuesta aceptada, el bloque nace ya con ellos.
    volumeTargets: options.volumeTargets ?? {},
    // `null` no es «no se sabe»: es «las puso la plantilla». Ver la 0030.
    sessionPlan: options.sessionPlan ?? null,
    // Congelada aquí: las semanas siguientes se reconstruyen con ESTA, no con
    // la del perfil de ese momento (0034).
    availability,
  });

  // Los parametros vienen del bloque construido, no de la tabla de
  // readaptacion: con fases distintas, releer esa tabla seria leer los
  // parametros de otro bloque.
  const weeks = built.weekParams.map((params) =>
    trainingWeekSchema.parse({
      ...newSyncColumns(ctx),
      blockId: block.id,
      weekIndex: params.weekIndex,
      weekStart: addDays(startDate, (params.weekIndex - 1) * 7),
      intent: params.intent,
      status: params.weekIndex === 1 ? 'active' : 'planned',
    }),
  );

  const blockRows: { table: LocalTable; row: SyncColumns }[] = [
    { table: 'trainingBlocks', row: block },
    ...weeks.map((week) => ({ table: 'trainingWeeks' as LocalTable, row: week as SyncColumns })),
  ];
  await putNewRows(ctx, blockRows);

  await materializeWeek(ctx, block, weeks[0] as TrainingWeek, built, equipment);

  return { block, builtBlock: built, warnings: built.warnings };
}

/**
 * Los ejercicios que un bloque YA tiene, por nombre de sesion.
 *
 * Es lo unico que sobrevive de una eleccion: el indice del hueco no se guarda
 * en ningun sitio y no se puede deducir del orden, porque `ordenarSesion`
 * reordena por rol y prioridad antes de escribir. Por eso `preferredExercises`
 * es una lista por sesion y no un mapa por hueco.
 *
 * Se lee de TODAS las sesiones del bloque, no solo de la primera semana: si la
 * semana 1 se borrara, la 2 sigue siendo una referencia valida de lo que este
 * bloque decidio.
 */
export async function exercisesOfBlock(blockId: string): Promise<Map<string, string[]>> {
  const sesiones = (await db.trainingSessions.where('blockId').equals(blockId).toArray()).filter(
    (sesion) => sesion.deletedAt === null,
  );

  const porNombre = new Map<string, string[]>();
  // De antigua a moderna, para que la primera semana mande sobre las
  // siguientes: es la que se materializo con la eleccion original.
  for (const sesion of [...sesiones].sort((a, b) =>
    a.scheduledDate.localeCompare(b.scheduledDate),
  )) {
    if (porNombre.has(sesion.name)) continue;
    const ejercicios = (
      await db.sessionExercises.where('sessionId').equals(sesion.id).toArray()
    ).filter((fila) => fila.deletedAt === null);
    if (ejercicios.length === 0) continue;
    porNombre.set(
      sesion.name,
      [...ejercicios].sort((a, b) => a.orderIndex - b.orderIndex).map((fila) => fila.exerciseSlug),
    );
  }

  return porNombre;
}

/** Crea las sesiones, ejercicios y series prescritas de una semana concreta. */
export async function materializeWeek(
  ctx: WriteContext,
  block: TrainingBlock,
  week: TrainingWeek,
  built: BuiltBlock,
  equipment: { id: string; kind: string }[],
): Promise<void> {
  const existing = await listSessionsOfWeek(week.id);
  if (existing.length > 0) return;

  const templates = built.sessions.filter((session) => session.weekIndex === week.weekIndex);
  const entries: { table: LocalTable; row: SyncColumns }[] = [];
  /*
   * Donde cae esta semana para el §11, y de donde NO puede salir.
   *
   * Se contaban los `weekIndex` distintos de `built.sessions`, y eso era falso
   * a partir de la semana 2: `materializarSemana` llama aqui con las sesiones
   * YA FILTRADAS por la semana que toca —`{ ...built, sessions: ajustada.
   * sessions }`—, asi que el conjunto tenia un solo `weekIndex`, el total valia
   * 1 y el progreso salia 1 SIEMPRE. Con el progreso clavado en el final,
   * la ventana de «acumulacion tardia» no descartaba nada y los drops empezaban en la
   * semana 2 de 8. Solo la semana 1 estaba bien, porque la materializa
   * `createBlockForPhase` pasando el bloque entero.
   *
   * No fallaba ni avisaba: aparecian drops plausibles, antes de tiempo. Es la
   * misma forma que `Object.entries` sobre un array, un valor derivado de una
   * coleccion que no tiene la forma que la derivacion supone.
   *
   * Ahora lo calcula el dominio a partir de la fase y la semana, que no
   * dependen de con que se llame a esta funcion.
   */
  const acumulacionRestante = accumulationWeeksLeftOf(
    block.phase,
    week.weekIndex,
    block.plannedWeeks,
  );

  for (const template of templates) {
    // El techo de drops del §11 es POR SESION, asi que el contador se reinicia
    // con cada una.
    let dropsEnLaSesion = 0;
    const session = trainingSessionSchema.parse({
      ...newSyncColumns(ctx),
      weekId: week.id,
      blockId: block.id,
      scheduledDate: addDays(week.weekStart, dayOffsetFor(template, block.availability)),
      sessionKind: 'strength',
      name: template.name,
      objective: template.objective,
      orderInWeek: template.orderInWeek,
      estimatedMinutes: template.estimatedMinutes,
      status: 'planned',
      rescheduledFrom: null,
      lockedByDevice: null,
      lockedAt: null,
    });
    entries.push({ table: 'trainingSessions', row: session });

    for (const item of template.exercises) {
      const exercise = requireExercise(item.slug);
      const equipmentId =
        equipment.find((candidate) => candidate.kind === exercise.equipmentKind)?.id ?? null;

      // La tecnica del §11, decidida aqui y no cableada.
      //
      // El `readiness` va en `null` A PROPOSITO: esto corre al materializar la
      // semana, dias antes de entrenar, y el readiness del dia todavia no
      // existe. La segunda mitad de esa regla la aplica `seriePermitidaHoy`
      // cuando la serie va a hacerse de verdad. Adivinarlo aqui seria
      // inventarse el dato; no prescribir nunca por si acaso apagaria la
      // tecnica entera, porque planificar siempre va por delante.
      const decision = chooseTechnique({
        role: item.role,
        phase: block.phase,
        intent: template.intent,
        accumulationWeeksLeft: acumulacionRestante,
        readiness: null,
        technicalDemand: exercise.technicalDemand,
        axialLoad: exercise.axialLoad,
        isCompound: exercise.isCompound,
        onStableMachine:
          exercise.equipmentKind === 'machine_stack' || exercise.equipmentKind === 'cable',
        // Sin ningun sitio donde declarar prisa, no se supone ninguna: el §11
        // reserva rest-pause y myo-reps para «cuando falta tiempo», y darlo por
        // hecho seria usarlas por defecto, justo lo que la regla dura prohibe.
        timeConstrained: false,
        dropsYaEnLaSesion: dropsEnLaSesion,
      });
      if (decision.technique === 'drop') dropsEnLaSesion += 1;

      const sessionExercise = sessionExerciseSchema.parse({
        ...newSyncColumns(ctx),
        sessionId: session.id,
        exerciseSlug: item.slug,
        equipmentId,
        orderIndex: item.orderIndex,
        role: item.role,
        technique: decision.technique,
        supersetGroup: null,
        targetRestSeconds: item.restSeconds,
        // El §11 exige justificacion registrada para incluir cualquier tecnica.
        // Aqui es donde queda escrita, junto a la fila que la lleva.
        notes: decision.technique === 'straight' ? null : decision.reasonCodes.join(' '),
        substitutedFromSlug: null,
      });
      entries.push({ table: 'sessionExercises', row: sessionExercise });

      const tipos = expandSets(decision.technique, item.sets);
      for (const [indice, setType] of tipos.entries()) {
        const setIndex = indice + 1;
        const prescription = setPrescriptionSchema.parse({
          ...newSyncColumns(ctx),
          sessionExerciseId: sessionExercise.id,
          setIndex,
          setType,
          targetRepsMin: item.target.repsMin,
          targetRepsMax: item.target.repsMax,
          targetRir: item.target.rir,
          targetLoadKg: null,
          restSeconds: item.restSeconds,
        });
        entries.push({ table: 'setPrescriptions', row: prescription });
      }
    }
  }

  await putNewRows(ctx, entries);
}

/**
 * Cierra un bloque agotado.
 *
 * Nada lo hacia hasta ahora: los bloques se quedaban `active` para siempre, y
 * como `createReadaptationBlock` solo se ofrece cuando NO hay bloque activo, al
 * terminar la ultima semana la aplicacion se quedaba sin nada que ofrecer y sin
 * forma de salir de ahi.
 */
export async function closeBlock(
  ctx: WriteContext,
  block: TrainingBlock,
  endedOn: string,
  status: 'completed' | 'aborted' = 'completed',
): Promise<TrainingBlock> {
  const semanas = await listWeeks(block.id);
  return putRow(
    ctx,
    'trainingBlocks',
    trainingBlockSchema.parse({
      ...block,
      status,
      endedOn,
      // Lo que de verdad se entreno, que puede no coincidir con lo previsto si
      // el bloque se alargo o se acorto (§2.2).
      actualWeeks: semanas.filter((semana) => semana.status === 'completed').length,
    }),
  );
}

/** Dias que faltan para que el bloque se agote. Negativo si ya paso. */
export function blockDaysLeft(block: TrainingBlock, today: string): number {
  const fin = addDays(block.startedOn, block.plannedWeeks * 7);
  return Math.round(
    (new Date(`${fin}T00:00:00Z`).getTime() - new Date(`${today}T00:00:00Z`).getTime()) / 86_400_000,
  );
}

// --------------------------------------------------------------------------
// Materializacion de la semana en curso
// --------------------------------------------------------------------------

export interface EnsureWeekResult {
  weekIndex: number;
  sessions: TrainingSession[];
  /** Ajustes de volumen aplicados al construirla. */
  volumeChanges: VolumeFitChange[];
  /** Cardio prescrito para la semana. */
  cardio: CardioSlot[];
  reasonCodes: string[];
}

/**
 * Crea las sesiones de la semana en la que cae `today`, si no existen ya.
 *
 * Hasta ahora `materializeWeek` solo se llamaba al crear el bloque, asi que las
 * semanas 2 en adelante no llegaban a existir NUNCA: al terminar la primera, la
 * pantalla de entrenar se quedaba vacia. Las filas de `training_weeks` si se
 * creaban por adelantado; lo que faltaba eran las sesiones.
 *
 * Se materializa de una en una a proposito (ver la cabecera del modulo): el
 * contenido de una semana depende de como fue la anterior. Es justo aqui donde
 * entra el objetivo de volumen del bloque, que es lo que hace que una propuesta
 * de «+2 series de pectoral» acabe siendo dos series de verdad.
 */
/**
 * Materializacion en curso, si la hay.
 *
 * El mismo problema que en la revision semanal: React ejecuta los efectos dos
 * veces en desarrollo, y dos pasadas solapadas comprueban las dos que no hay
 * sesiones ANTES de que ninguna las haya creado. El resultado serian dos
 * semanas de sesiones duplicadas, que es bastante peor que dos propuestas
 * repetidas.
 */
let materializando: Promise<EnsureWeekResult | null> | null = null;

export async function ensureWeekMaterialized(
  ctx: WriteContext,
  today: string,
): Promise<EnsureWeekResult | null> {
  if (materializando !== null) return materializando;
  materializando = materializarSemana(ctx, today).finally(() => {
    materializando = null;
  });
  return materializando;
}

async function materializarSemana(
  ctx: WriteContext,
  today: string,
): Promise<EnsureWeekResult | null> {
  const block = await getActiveBlock(ctx.userId);
  if (!block) return null;

  const weeks = await listWeeks(block.id);
  const deHoy = weeks.find(
    (fila) => fila.weekStart <= today && today < addDays(fila.weekStart, 7),
  );
  // Fuera del bloque: o no ha empezado, o ya termino. Cerrar un bloque es otra
  // decision y no se toma aqui.
  if (!deHoy) return null;

  // Una semana POSTERIOR ya activa es una semana preparada de antemano, al
  // cerrar la de hoy (`weekCloseService`). Se trabaja sobre ella y no se
  // reactiva la de hoy: el bucle de estados de abajo la volveria a poner activa
  // y habria dos semanas activas a la vez, con la pantalla enseñando la vieja.
  const week =
    weeks.find((fila) => fila.status === 'active' && fila.weekIndex > deHoy.weekIndex) ?? deHoy;

  // OJO: no se sale por aqui aunque las sesiones existan. La semana 1 se
  // materializa dentro de `createReadaptationBlock`, asi que si esta funcion
  // se rindiera al ver sesiones, la primera semana se quedaria SIN cardio para
  // siempre. Lo que se salta mas abajo es solo la creacion de sesiones.
  const existentes = await listSessionsOfWeek(week.id);
  const yaMaterializada = existentes.length > 0;

  const gym = await getDefaultGym(ctx.userId);
  const equipment = gym ? await listEquipment(gym.id) : [];
  const kinds = [...new Set(equipment.map((item) => item.kind))];

  // El bloque se reconstruye en lugar de guardarse: `buildPhaseBlock` es
  // determinista dado el equipamiento, asi que reconstruirlo da lo mismo que
  // haberlo guardado, y ademas recoge un gimnasio que haya cambiado.
  //
  // Se reconstruye con LA FASE DEL BLOQUE, no con readaptacion fija: en cuanto
  // exista un segundo bloque, dar por hecha la readaptacion le pondria los
  // parametros de otra fase.
  // Los ejercicios que el bloque YA tiene, recuperados de las semanas
  // materializadas. Sin esto, un bloque cuyos ejercicios eligio la IA saldria
  // con los del motor a partir de la semana 2: la reconstruccion solo daba lo
  // mismo que guardarlo mientras `buildBlock` fuera determinista dado el
  // equipamiento, y una eleccion externa rompe esa condicion.
  //
  // Sirve ademas para algo que no tiene que ver con la IA: si el usuario añade
  // una maquina a mitad de bloque, la semana 3 dejaba de parecerse a la 1 sin
  // que nadie lo hubiera pedido. Ahora el bloque conserva sus ejercicios y el
  // equipamiento nuevo entra en el siguiente.
  const yaElegidos = await exercisesOfBlock(block.id);

  const built = buildPhaseBlock(block.phase, {
    availableEquipmentKinds: kinds,
    musclePriorities: block.musclePriorities,
    preferredExercises: (sessionName) => yaElegidos.get(sessionName) ?? [],
    // LA PIEZA QUE HACE QUE EL DISEÑO SOBREVIVA A LA SEMANA 1.
    //
    // Sin esto, un bloque cuyas sesiones diseñó la IA se rematerializaría con
    // las cuatro de `splits.ts` a partir de la semana 2: la reconstrucción no
    // tiene forma de saber que hubo otra cosa, y el bloque cambiaría de forma a
    // mitad sin que nadie lo pidiera. `null` en un bloque anterior a la 0030
    // significa «las puso la plantilla», así que ahí no cambia nada.
    sessions: block.sessionPlan ?? undefined,
    // La disponibilidad CONGELADA del bloque, no la del perfil: cambiarla a
    // mitad no debe cambiar la estructura de lo que queda. Las limitaciones,
    // en cambio, las ACTUALES: una lesión declarada a mitad de bloque tiene
    // que dejar de cargarse ya en la semana siguiente.
    availability: block.availability,
    limitations: (await getProfile(ctx.userId))?.limitations ?? [],
  });

  const deLaSemana = built.sessions.filter((sesion) => sesion.weekIndex === week.weekIndex);
  const ajustada = fitWeeklyVolume({
    sessions: deLaSemana,
    // `?? {}` ademas de la normalizacion: si alguien llama a esta funcion con un
    // bloque que no paso por `getActiveBlock`, no se cae la pantalla entera.
    targets: block.volumeTargets ?? {},
    maxSessionMinutes: block.maxSessionMinutes,
    musclePriorities: block.musclePriorities,
  });

  if (!yaMaterializada) {
    await materializeWeek(
      ctx,
      block,
      week,
      { ...built, sessions: ajustada.sessions },
      equipment,
    );
  }

  // La semana que se acaba de materializar pasa a ser la activa, y las
  // anteriores se dan por cerradas: dos semanas activas a la vez no significan
  // nada.
  for (const fila of weeks) {
    if (fila.weekIndex === week.weekIndex && fila.status !== 'active') {
      await putRow(
        ctx,
        'trainingWeeks',
        parchear(trainingWeekSchema, fila, { status: 'active' }, {
          tabla: 'training_weeks',
          id: fila.id,
        }),
      );
    } else if (fila.weekIndex < week.weekIndex && fila.status === 'active') {
      await putRow(
        ctx,
        'trainingWeeks',
        parchear(trainingWeekSchema, fila, { status: 'completed' }, {
          tabla: 'training_weeks',
          id: fila.id,
        }),
      );
    }
  }

  // --- cardio de la semana (3.9) --------------------------------------------
  // Se coloca DESPUES de la fuerza, y no al reves, porque la regla del §8 mira
  // la fuerza para decidir: el cardio no puede caer la vispera de una pierna
  // importante, asi que primero tiene que haber piernas colocadas.
  const cardio = placeCardio({
    weekStart: week.weekStart,
    plan: planWeeklyCardio({
      phase: block.phase,
      weekIndex: week.weekIndex,
      plannedWeeks: block.plannedWeeks,
    }),
    strengthSessions: ajustada.sessions.map((sesion) => comoWeekSession(sesion, week.weekStart, block.availability)),
    phase: block.phase,
    // No se prescribe cardio en dias que ya pasaron.
    notBefore: today > week.weekStart ? today : undefined,
  });
  await saveCardioPrescriptions(ctx, cardio);

  return {
    weekIndex: week.weekIndex,
    sessions: await listSessionsOfWeek(week.id),
    // Si ya estaba materializada, lo que se devuelve NO son los ajustes que se
    // aplicaron en su dia: son los que saldrian ahora. Se informan como vacios
    // para no dar por hecho algo que no se ha comprobado.
    volumeChanges: yaMaterializada ? [] : ajustada.changes,
    cardio,
    reasonCodes: yaMaterializada ? ['already_materialized'] : ajustada.reasonCodes,
  };
}

/**
 * Adapta una sesion resuelta a lo que necesitan las reglas de calendario.
 *
 * `placeCardio` y `reorganizeWeek` trabajan con `WeekSession`, que es la vista
 * de una sesion desde el punto de vista del calendario: cuando cae y que
 * musculos mueve. No les hace falta la lista de ejercicios.
 */
function comoWeekSession(
  session: ResolvedSession,
  weekStart: string,
  availability: Availability | null,
): WeekSession {
  const volumen = weeklyVolumeByMuscle([session]);
  const volumeByMuscle: Partial<Record<Muscle, number>> = {};
  for (const [muscle, series] of volumen) {
    volumeByMuscle[muscle as Muscle] = series;
  }

  return {
    id: `${session.weekIndex}-${session.orderInWeek}`,
    name: session.name,
    scheduledDate: addDays(weekStart, dayOffsetFor(session, availability)),
    status: 'planned',
    sessionKind: 'strength',
    estimatedMinutes: session.estimatedMinutes,
    volumeByMuscle,
  };
}

/**
 * Lunes, martes, jueves y viernes: los días de un bloque anterior al test
 * inicial, que no guarda disponibilidad. Con ella, mandan los días declarados
 * (`dayOffsets` del dominio, que da exactamente esto para esos cuatro).
 */
const DAY_OFFSETS_SIN_DISPONIBILIDAD = [0, 1, 3, 4];

function dayOffsetFor(
  session: { orderInWeek: number },
  availability: Availability | null,
): number {
  const dias = availability === null ? DAY_OFFSETS_SIN_DISPONIBILIDAD : dayOffsets(availability);
  return dias[session.orderInWeek] ?? session.orderInWeek;
}

/** Series de aproximacion ya persistidas para un ejercicio de la sesion. */
export async function listWarmups(sessionExerciseId: string): Promise<WarmupPrescription[]> {
  const rows = await db.warmupPrescriptions
    .where('sessionExerciseId')
    .equals(sessionExerciseId)
    .toArray();
  return rows
    .filter((row) => row.deletedAt === null)
    .sort((a, b) => a.orderIndex - b.orderIndex);
}

/** Guarda las series de aproximacion calculadas al empezar la sesion. */
export async function saveWarmups(
  ctx: WriteContext,
  sessionExerciseId: string,
  warmups: { orderIndex: number; loadKg: number; reps: number; restSeconds: number }[],
): Promise<void> {
  const rows = warmups.map((warmup) =>
    warmupPrescriptionSchema.parse({
      ...newSyncColumns(ctx),
      sessionExerciseId,
      orderIndex: warmup.orderIndex,
      loadKg: warmup.loadKg,
      reps: warmup.reps,
      restSeconds: warmup.restSeconds,
      isCompleted: false,
    }),
  );
  await putNewRows(
    ctx,
    rows.map((row) => ({ table: 'warmupPrescriptions' as const, row })),
  );
}

export async function markWarmupCompleted(
  ctx: WriteContext,
  warmup: WarmupPrescription,
): Promise<void> {
  await putRow(ctx, 'warmupPrescriptions', { ...warmup, isCompleted: true });
}

export async function updateSession(
  ctx: WriteContext,
  session: TrainingSession,
  patch: Partial<TrainingSession>,
): Promise<TrainingSession> {
  return putRow(
    ctx,
    'trainingSessions',
    parchear(trainingSessionSchema, session, patch, {
      tabla: 'training_sessions',
      id: session.id,
    }),
  );
}

/** Sesiones de una semana concreta, ordenadas por dia. */
/**
 * Volumen prescrito por musculo de una sesion, en series.
 *
 * Cuenta lo PRESCRITO, no lo realizado: se usa para decidir que clase de dia
 * es —de pierna, de empuje— y esa decision hay que tomarla antes de entrenar.
 * Las secundarias cuentan media serie, como en `weeklyVolumeByMuscle`.
 */
export async function prescribedVolumeByMuscle(
  sessionId: string,
): Promise<Partial<Record<Muscle, number>>> {
  const ejercicios = (await db.sessionExercises.where('sessionId').equals(sessionId).toArray())
    .filter((fila) => fila.deletedAt === null);

  const volumen: Partial<Record<Muscle, number>> = {};
  for (const ejercicio of ejercicios) {
    const catalogo = findExercise(ejercicio.exerciseSlug);
    if (!catalogo) continue;

    const series = (
      await db.setPrescriptions.where('sessionExerciseId').equals(ejercicio.id).toArray()
    ).filter((fila) => fila.deletedAt === null).length;
    if (series === 0) continue;

    for (const muscle of catalogo.primaryMuscles) {
      volumen[muscle] = (volumen[muscle] ?? 0) + series;
    }
    for (const muscle of catalogo.secondaryMuscles) {
      volumen[muscle] = (volumen[muscle] ?? 0) + series * 0.5;
    }
  }
  return volumen;
}

/**
 * Las sesiones de una semana como `WeekSession`, con su volumen por musculo.
 *
 * Vive aqui, y no en el servicio que la estrenó, porque la necesitan dos
 * consumidores que no se conocen: la revision semanal y el tipo de dia
 * nutricional. Dos copias acabarian discrepando sobre que es un dia de pierna.
 */
export async function weekSessions(userId: string, weekStart: string): Promise<WeekSession[]> {
  const filas = await listSessionsOfDateRange(userId, weekStart, addDays(weekStart, 7));
  return Promise.all(
    filas.map(async (fila) => ({
      id: fila.id,
      name: fila.name,
      scheduledDate: fila.scheduledDate,
      status: fila.status,
      sessionKind: fila.sessionKind,
      estimatedMinutes: fila.estimatedMinutes,
      volumeByMuscle: await prescribedVolumeByMuscle(fila.id),
    })),
  );
}

export async function listSessionsOfDateRange(
  userId: string,
  from: string,
  to: string,
): Promise<TrainingSession[]> {
  const rows = await db.trainingSessions.where('userId').equals(userId).toArray();
  return rows
    .filter(
      (row) => row.deletedAt === null && row.scheduledDate >= from && row.scheduledDate < to,
    )
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
}

/**
 * Marca como perdidas las sesiones planificadas cuyo dia ya paso.
 *
 * El §8 dispara la reorganizacion «al fin del dia con status = planned». No hay
 * ningun proceso de fondo que lo haga —la aplicacion solo corre cuando el
 * usuario la abre— asi que se hace al revisar la semana, que es el momento en
 * el que alguien mira. Dejarlas en `planned` para siempre haria que una sesion
 * de hace tres semanas siguiera figurando como pendiente.
 *
 * No toca las de HOY: el dia no ha terminado.
 */
export async function markMissedSessions(
  ctx: WriteContext,
  today: string,
): Promise<TrainingSession[]> {
  const rows = await db.trainingSessions.where('userId').equals(ctx.userId).toArray();
  const perdidas = rows.filter(
    (row) => row.deletedAt === null && row.status === 'planned' && row.scheduledDate < today,
  );

  const actualizadas: TrainingSession[] = [];
  for (const row of perdidas) {
    actualizadas.push(await updateSession(ctx, row, { status: 'missed' }));
  }
  return actualizadas;
}

/**
 * Deja constancia del turno en el que se entrena ESTA sesion.
 *
 * Es escritura en `training_sessions` y nunca fue nutricional, por eso vive
 * aqui y no en `nutritionService`.
 *
 * PARA QUE SIRVE: para que el historial sepa **cuando** se levanto cada cosa
 * (§10 del encargo: comparar rendimientos en condiciones equivalentes) y, desde
 * que la nutricion volvio el 28/09/2026, para repartir los hidratos del dia
 * alrededor de la sesion. Quien cambia el turno desde la ficha de la sesion
 * llama a `nutritionService.setSessionTrainingTime`, que escribe con esta
 * funcion y despues rehace los menus futuros.
 */
export async function setSessionTrainingTime(
  ctx: WriteContext,
  session: TrainingSession,
  timing: TrainingTimeOfDay,
): Promise<void> {
  if (session.trainingTimeOfDay === timing) return;
  await updateSession(ctx, session, { trainingTimeOfDay: timing });
}
